/**
 * PATH: packages/pricelist/src/ColumnDialog.tsx
 * FILE_ID: PKG-PRICELIST-COLDLG-001
 * PURPOSE: Minimal, dependency-light modal to add a custom/computed column to
 *          a section's column_schema (label, type, optional "=formula").
 * WHY: The grid is universal — users extend any section with their own Excel
 *      columns. Kept self-contained (no design-system import) so every host
 *      can mount it without wiring shadcn primitives.
 * RELATED: types.ts (ColumnDef), usePricelistWorkbook (runColumnOp)
 */
import { useState } from 'react'
import type { ColumnDef } from './types'
import type { FormulaField } from './formulaFields'

export interface ColumnDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (column: ColumnDef) => void
  /** Fields a formula can reference, rendered as click-to-insert chips. */
  availableFields?: FormulaField[]
}

const TYPES: Array<{ value: NonNullable<ColumnDef['data_type']>; label: string }> = [
  { value: 'number', label: 'Number' },
  { value: 'money', label: 'Money' },
  { value: 'text', label: 'Text' },
  { value: 'enum', label: 'Dropdown' },
  { value: 'bool', label: 'Yes/No' },
]

const slug = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'column'

export default function ColumnDialog({
  open,
  onClose,
  onSubmit,
  availableFields = [],
}: ColumnDialogProps) {
  const [label, setLabel] = useState('')
  const [dataType, setDataType] = useState<NonNullable<ColumnDef['data_type']>>('number')
  const [computed, setComputed] = useState('')
  const [enumValues, setEnumValues] = useState('')

  const insertField = (key: string) =>
    setComputed((prev) => {
      const base = prev.trim() || '='
      return base === '=' ? `= ${key}` : `${base} ${key}`
    })

  if (!open) return null

  const submit = () => {
    if (!label.trim()) return
    const column: ColumnDef = {
      key: slug(label),
      label: label.trim(),
      data_type: dataType,
    }
    if (computed.trim()) column.computed_from = computed.trim()
    if (dataType === 'enum')
      column.enum_values = enumValues.split(',').map((v) => v.trim()).filter(Boolean)
    onSubmit(column)
    setLabel('')
    setComputed('')
    setEnumValues('')
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3 text-base font-semibold text-slate-900 dark:text-white">Add column</h3>
        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Label</label>
        <input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Polish rate"
          className="mb-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        />
        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Type</label>
        <select
          value={dataType}
          onChange={(e) => setDataType(e.target.value as NonNullable<ColumnDef['data_type']>)}
          className="mb-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        >
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {dataType === 'enum' && (
          <input
            value={enumValues}
            onChange={(e) => setEnumValues(e.target.value)}
            placeholder="Comma,separated,options"
            className="mb-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
          />
        )}
        <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
          Computed formula (optional)
        </label>
        <input
          value={computed}
          onChange={(e) => setComputed(e.target.value)}
          placeholder="=base_cost * (1 + markup_percent/100)"
          className="mb-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        />
        {availableFields.length > 0 && (
          <div className="mb-1 flex flex-wrap gap-1">
            {availableFields.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => insertField(f.key)}
                title={`Insert ${f.key} (${f.label})`}
                className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-emerald-500"
              >
                {f.key}
              </button>
            ))}
          </div>
        )}
        <p className="mb-4 text-[11px] text-slate-400">
          Click a field to insert it, or type an Excel-style formula — e.g.
          <code className="mx-1">=base_cost * (1 + markup_percent/100)</code>.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!label.trim()}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            Add column
          </button>
        </div>
      </div>
    </div>
  )
}
